import { Loader2, Zap } from 'lucide-react';

export const PICKUP_MIN_LEAD_MS = 15 * 60 * 1000;

export function isSlotBookable(slot, now = Date.now()) {
  return new Date(slot.time).getTime() >= now + PICKUP_MIN_LEAD_MS;
}

function formatSlotTime(iso) {
  return new Date(iso).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'America/Toronto',
  });
}

function torontoDateKey(value) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Toronto',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(value));
}

function nextAvailableMessage(nextSlots, now) {
  const next = (nextSlots || []).find((s) => !s.is_full && isSlotBookable(s, now));
  if (!next) return null;
  const isToday = torontoDateKey(next.time) === torontoDateKey(now);
  return `Next available pickup is at ${isToday ? '' : 'tomorrow '}${formatSlotTime(next.time)}`;
}

export default function PickupTimeSelector({
  dates = [],
  selectedDate,
  onSelectDate,
  slots = [],
  skipSlots = [],
  nextAvailableSlots = [],
  now = Date.now(),
  loading,
  selectedTime,
  selectedSkipMinutes = null,
  onSelect,
  onSelectSkip,
  message,
}) {
  if (loading && (!dates || dates.length === 0)) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const bookableSlots = slots.filter((slot) => isSlotBookable(slot, now));
  const hasOpenSlot = bookableSlots.some((slot) => !slot.is_full);
  const emptyMessage =
    nextAvailableMessage(nextAvailableSlots, now) || message || 'No available time slots';

  return (
    <div className="space-y-4">
      {dates.length > 0 && (
        <div>
          <p className="text-sm font-medium mb-3 text-muted-foreground">Pickup day</p>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {dates.map((d) => {
              const isSelected = selectedDate === d.date;
              const disabled = d.is_closed;
              return (
                <button
                  key={d.date}
                  type="button"
                  disabled={disabled}
                  onClick={() => onSelectDate?.(d.date)}
                  className={`
                    flex-shrink-0 px-3 py-2 rounded-xl border-2 text-sm transition-all
                    ${isSelected
                      ? 'border-primary bg-primary/10 text-primary font-medium'
                      : 'border-transparent bg-muted hover:border-primary/30'
                    }
                    ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                  data-testid={`date-chip-${d.date}`}
                >
                  <span className="block whitespace-nowrap">{d.label}</span>
                  {disabled ? (
                    <span className="block text-[10px] text-muted-foreground">Closed</span>
                  ) : !d.has_bookable ? (
                    <span className="block text-[10px] text-muted-foreground">Full</span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {!loading && skipSlots.length > 0 && (
        <div>
          <p className="text-sm font-medium mb-3 text-muted-foreground">Skip the line</p>
          <div className="flex flex-col gap-2">
            {skipSlots.map((slot) => {
              const isSelected = selectedSkipMinutes === slot.minutes;
              return (
                <button
                  key={slot.minutes}
                  type="button"
                  onClick={() => onSelectSkip?.(slot)}
                  className={`
                    flex items-center gap-3 w-full px-4 py-3 rounded-xl border-2 text-left text-sm font-medium transition-all
                    ${isSelected
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-primary/60 bg-primary/10 text-primary hover:bg-primary/15'
                    }
                  `}
                  data-testid={`skip-slot-${slot.minutes}`}
                >
                  <Zap className="w-4 h-4 shrink-0 fill-current" />
                  <span>{slot.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <p className="text-sm font-medium mb-3 text-muted-foreground">Pickup time</p>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : !hasOpenSlot ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>{emptyMessage}</p>
            <p className="text-sm mt-1">Try another day</p>
          </div>
        ) : (
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {bookableSlots.map((slot) => {
              const isSelected = selectedSkipMinutes == null && selectedTime === slot.time;
              const isFull = slot.is_full;
              const capacity = slot.capacity || 1;
              const available = Math.max(0, slot.available ?? 0);

              return (
                <button
                  key={slot.time}
                  type="button"
                  disabled={isFull}
                  onClick={() => onSelect(slot.time)}
                  className={`
                    flex-shrink-0 flex flex-col items-center min-w-[4.75rem] px-2 py-3 rounded-xl border-2 transition-all
                    ${isSelected
                      ? 'border-primary bg-primary/10'
                      : 'border-transparent bg-muted hover:border-primary/30'
                    }
                    ${isFull ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                  data-testid={`time-slot-${slot.display.replace(/\s/g, '-')}`}
                >
                  <span className={`font-mono text-sm font-medium whitespace-nowrap ${isSelected ? 'text-primary' : ''}`}>
                    {slot.display}
                  </span>
                  <div className="mt-2 w-full max-w-[4.5rem]">
                    <div className="h-1 w-full rounded-full bg-muted-foreground/20 overflow-hidden">
                      <div
                        className={`h-full transition-all ${
                          available <= 1 ? 'bg-red-500' :
                          available <= 2 ? 'bg-yellow-500' : 'bg-green-500'
                        }`}
                        style={{ width: `${(available / capacity) * 100}%` }}
                      />
                    </div>
                    <span className="text-[10px] text-muted-foreground mt-1 block text-center">
                      {isFull ? 'Full' : `${available} left`}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
